import { ActionTree, GetterTree, MutationTree } from "vuex";
import { NolaCoinClient } from "@/lib/clients/nolaCoinClient";
import { Wallet, WalletBreakdown, CoinType } from "@/lib/models/nolaCoin";
import { logError } from "@/lib/utils/logger";
import { db, GeneralSettings as GeneralSettingsDexie } from "@/lib/indexeddb";

// Fish関連の型定義
export interface FishData {
  currentFish: number;
  maxFish: number;
  lastUpdated: Date;
  walletBreakdown: WalletBreakdown[];
}

// State型定義
export interface FishState {
  currentFish: number; // 現在のFish数
  maxFish: number; // 最大Fish数（月間上限など）
  isLoading: boolean; // ローディング状態
  error: string | null; // エラーメッセージ
  lastUpdated: Date | null; // 最終更新日時
  walletBreakdown: WalletBreakdown[]; // coinTypeごとの内訳
  isShowFishPurchaseCoachMark: boolean; // 購入ボタンのコーチマーク表示フラグ
}

// Mutations型定義
interface Mutations {
  setCurrentFish(state: FishState, fish: number): void;
  setMaxFish(state: FishState, fish: number): void;
  setFishData(state: FishState, data: FishData): void;
  subtractFish(state: FishState, amount: number): void;
  setLoading(state: FishState, loading: boolean): void;
  setError(state: FishState, error: string | null): void;
  setLastUpdated(state: FishState, date: Date): void;
  setWalletBreakdown(state: FishState, breakdown: WalletBreakdown[]): void;
  resetState(state: FishState): void;
}

// Actions型定義
interface Actions {
  fetchFishData(context: any): Promise<void>;
  consumeFishForRequest(context: any, amount: number): Promise<boolean>;
  resetFishData(context: any): Promise<void>;
  showFishPurchaseCoachMark(context: any): Promise<void>;
  closeFishPurchaseCoachMark(context: any): Promise<void>;
}

// Getters型定義
interface Getters {
  currentFish(state: FishState): number;
  maxFish(state: FishState): number;
  remainingFish(state: FishState): number;
  fishUsagePercentage(state: FishState): number;
  canConsumeFish(state: FishState): (amount: number) => boolean;
  isLoading(state: FishState): boolean;
  error(state: FishState): string | null;
  lastUpdated(state: FishState): Date | null;
  formattedCurrentFish(state: FishState): string;
  formattedMaxFish(state: FishState): string;
  formattedPrepaidFish(state: FishState): string;
  formattedFreeFish(state: FishState): string;
  walletBreakdown(state: FishState): WalletBreakdown[];
  getFishByCoinType(state: FishState): (coinType: CoinType) => number;
  baseFish(state: FishState): number;
  prepaidFish(state: FishState): number;
  bonusFish(state: FishState): number;
  freeFish(state: FishState): number;
  isShowFishPurchaseCoachMark(state: FishState): boolean;
}

// Mutations
const mutations: MutationTree<FishState> & Mutations = {
  setCurrentFish(state, fish) {
    state.currentFish = Math.max(0, fish); // 負の値を防ぐ
    state.lastUpdated = new Date();
  },
  setMaxFish(state, fish) {
    state.maxFish = Math.max(0, fish); // 負の値を防ぐ
    state.lastUpdated = new Date();
  },
  setFishData(state, data) {
    state.currentFish = Math.max(0, data.currentFish);
    state.maxFish = Math.max(0, data.maxFish);
    state.walletBreakdown = data.walletBreakdown;
    state.lastUpdated = data.lastUpdated;
  },

  subtractFish(state, amount) {
    const newAmount = state.currentFish - amount;
    state.currentFish = Math.max(0, newAmount); // 負の値を防ぐ
    state.lastUpdated = new Date();
  },
  setLoading(state, loading) {
    state.isLoading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  setLastUpdated(state, date) {
    state.lastUpdated = date;
  },
  setWalletBreakdown(state, breakdown) {
    state.walletBreakdown = breakdown;
  },
  resetState(state) {
    state.currentFish = 0;
    state.maxFish = 0; // API連携時は0からスタート
    state.isLoading = false;
    state.error = null;
    state.lastUpdated = null;
    state.walletBreakdown = [];
  },
  setIsShowFishPurchaseCoachMark(state, payload) {
    state.isShowFishPurchaseCoachMark = payload;
  },
};

// Actions
const actions: ActionTree<FishState, {}> & Actions = {
  async fetchFishData({ commit }) {
    try {
      commit("setLoading", true);
      commit("setError", null);

      // NolaCoin API からウォレット情報を取得
      const nolaCoinClient = new NolaCoinClient();
      const wallet: Wallet = await nolaCoinClient.getWallet();

      // Wallet データを Fish データにマッピング
      const fishData: FishData = {
        currentFish: wallet.amount, // Wallet残高 → 現在のFish数
        maxFish: wallet.totalAmount, // 総残高 → 最大Fish数として使用
        walletBreakdown: wallet.walletBreakdown, // coinTypeごとの内訳
        lastUpdated: new Date(),
      };

      commit("setFishData", fishData);
    } catch (error) {
      logError({ module: "Fish", action: "fetchFishData" }, "NolaCoin API エラー", error);

      // API エラー時はフォールバック用のモックデータを使用
      const fallbackData: FishData = {
        currentFish: 0,
        maxFish: 0,
        walletBreakdown: [],
        lastUpdated: new Date(),
      };

      commit("setFishData", fallbackData);
      commit("setError", "Fish データの取得に失敗しました。オフラインモードで表示しています。");
    } finally {
      commit("setLoading", false);
    }
  },

  async consumeFishForRequest({ commit, state }, amount) {
    try {
      if (state.currentFish < amount) {
        commit("setError", "Fish が不足しています");
        return false;
      }

      commit("setError", null);
      commit("subtractFish", amount);
      return true;
    } catch (error) {
      commit("setError", "Fish の消費に失敗しました");
      return false;
    }
  },

  async resetFishData({ commit }) {
    commit("resetState");
  },

  async showFishPurchaseCoachMark({ commit }) {
    const record = await db.transaction(
      "readonly",
      db.generalSettings,
      async () => await db.generalSettings.get("isShowFishPurchaseCoachMark")
    );

    if (!record) {
      // state の変更のみ行う
      commit("setIsShowFishPurchaseCoachMark", true);
    }
  },
  async closeFishPurchaseCoachMark({ commit }) {
    commit("setIsShowFishPurchaseCoachMark", false);

    await db.transaction("readwrite", db.generalSettings, async () => {
      const record = await db.generalSettings.get("isShowFishPurchaseCoachMark");
      if (!record) {
        // indexedDB の isShowFishPurchaseCoachMark には false のみが入る
        await db.generalSettings.put(new GeneralSettingsDexie(false), "isShowFishPurchaseCoachMark");
      }
    });
  },
};

// Getters
const getters: GetterTree<FishState, {}> & Getters = {
  currentFish: (state) => state.currentFish,
  maxFish: (state) => state.maxFish,
  remainingFish: (state) => state.maxFish - state.currentFish,
  fishUsagePercentage: (state) => {
    if (state.maxFish === 0) return 0;
    return Math.round((state.currentFish / state.maxFish) * 100);
  },
  canConsumeFish: (state) => (amount: number) => state.currentFish >= amount,
  isLoading: (state) => state.isLoading,
  error: (state) => state.error,
  lastUpdated: (state) => state.lastUpdated,
  formattedCurrentFish: (state) => state.currentFish.toLocaleString("ja-JP"),
  formattedMaxFish: (state) => state.maxFish.toLocaleString("ja-JP"),
  formattedPrepaidFish: (state) => {
    const breakdown = state.walletBreakdown.find((item) => item.coinType === CoinType.PREPAID_WEB);
    const amount = breakdown ? breakdown.amount : 0;
    return amount.toLocaleString("ja-JP");
  },
  formattedFreeFish: (state) => {
    const baseBreakdown = state.walletBreakdown.find((item) => item.coinType === CoinType.BASE);
    const bonusBreakdown = state.walletBreakdown.find((item) => item.coinType === CoinType.BONUS);
    const baseAmount = baseBreakdown ? baseBreakdown.amount : 0;
    const bonusAmount = bonusBreakdown ? bonusBreakdown.amount : 0;
    const totalAmount = baseAmount + bonusAmount;
    return totalAmount.toLocaleString("ja-JP");
  },
  walletBreakdown: (state) => state.walletBreakdown,
  getFishByCoinType: (state) => (coinType: CoinType) => {
    const breakdown = state.walletBreakdown.find((item) => item.coinType === coinType);
    return breakdown ? breakdown.amount : 0;
  },
  baseFish: (state) => {
    const breakdown = state.walletBreakdown.find((item) => item.coinType === CoinType.BASE);
    return breakdown ? breakdown.amount : 0;
  },
  prepaidFish: (state) => {
    const breakdown = state.walletBreakdown.find((item) => item.coinType === CoinType.PREPAID_WEB);
    return breakdown ? breakdown.amount : 0;
  },
  bonusFish: (state) => {
    const breakdown = state.walletBreakdown.find((item) => item.coinType === CoinType.BONUS);
    return breakdown ? breakdown.amount : 0;
  },
  freeFish: (state) => {
    const baseBreakdown = state.walletBreakdown.find((item) => item.coinType === CoinType.BASE);
    const bonusBreakdown = state.walletBreakdown.find((item) => item.coinType === CoinType.BONUS);
    const baseAmount = baseBreakdown ? baseBreakdown.amount : 0;
    const bonusAmount = bonusBreakdown ? bonusBreakdown.amount : 0;
    return baseAmount + bonusAmount;
  },
  isShowFishPurchaseCoachMark: (state) => state.isShowFishPurchaseCoachMark && state.currentFish < 5000,
};

// 初期状態
const state: FishState = {
  currentFish: 0,
  maxFish: 0, // API連携により動的取得
  isLoading: false,
  error: null,
  lastUpdated: null,
  walletBreakdown: [],
  isShowFishPurchaseCoachMark: false,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
